import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import {
  signIn,
  signInWithRedirect,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { User } from '../types/user';
import { awsConfig, validateAwsConfig, isFederationEnabled } from '../aws-config';

// Configure Amplify
const isConfigValid = validateAwsConfig();
if (isConfigValid) {
  Amplify.configure(awsConfig);
}

/**
 * Authentication context type defining the shape of auth state and methods.
 */
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  isFederationEnabled: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithFederate: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AUTH_USER_KEY = 'auth_user';

const defaultContextValue: AuthContextType = {
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  isLoading: true,
  isFederationEnabled,
  login: async () => {
    throw new Error('AuthProvider not found');
  },
  loginWithFederate: async () => {
    throw new Error('AuthProvider not found');
  },
  logout: async () => {
    throw new Error('AuthProvider not found');
  },
  getToken: async () => null,
};

export const AuthContext = createContext<AuthContextType>(defaultContextValue);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Map a Cognito session into our internal User shape. Federated users
 * have their useful identity data in the ID token's claims rather than
 * the `username` (which is `Federate_<alias>` for federated identities).
 */
async function buildUserFromSession(): Promise<User> {
  const currentUser = await getCurrentUser();
  const session = await fetchAuthSession();
  const attrs = await fetchUserAttributes();

  const email = attrs.email || currentUser.signInDetails?.loginId || '';
  const givenName = attrs.given_name || '';
  const familyName = attrs.family_name || '';
  const fullName = attrs.name || `${givenName} ${familyName}`.trim() || currentUser.username;

  // Cognito groups (admin role) come from the access token, not ID token.
  const groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[] | undefined) ?? [];

  return {
    id: currentUser.userId,
    email,
    name: fullName,
    role: groups.includes('admin') ? 'admin' : 'user',
    enabled: true,
    createdAt: new Date().toISOString(),
    lastLogin: new Date().toISOString(),
  };
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check existing session and listen for sign-in events from the
  // post-redirect callback. Hub fires 'signedIn' when Amplify finishes
  // exchanging the OAuth code for tokens.
  useEffect(() => {
    let cancelled = false;

    const refreshUser = async () => {
      try {
        const cognitoUser = await buildUserFromSession();
        if (cancelled) return;
        setUser(cognitoUser);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(cognitoUser));
      } catch {
        if (cancelled) return;
        localStorage.removeItem(AUTH_USER_KEY);
        setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (!isConfigValid) {
      console.log('Cognito not configured, using dev mode auto-login');
      // Auto-login with a dev user when Cognito is not configured
      const devUser: User = {
        id: 'dev-user-001',
        email: 'developer@local.dev',
        name: 'Dev User',
        role: 'admin',
        enabled: true,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
      };
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(devUser));
      setUser(devUser);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    refreshUser();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
          refreshUser();
          break;
        case 'signedOut':
          localStorage.removeItem(AUTH_USER_KEY);
          setUser(null);
          break;
        case 'signInWithRedirect_failure':
          console.error('Sign in with redirect failed', payload);
          setIsLoading(false);
          break;
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<void> => {
    setIsLoading(true);

    try {
      // If Cognito is not configured, use mock auth
      if (!isConfigValid) {
        await new Promise((resolve) => setTimeout(resolve, 500));

        if (!username || !password) {
          throw new Error('Username and password are required');
        }

        const mockUser: User = {
          id: `user-${Date.now()}`,
          email: username.includes('@') ? username : `${username}@example.com`,
          name: username.split('@')[0],
          role: username.includes('admin') ? 'admin' : 'user',
          enabled: true,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
        };

        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(mockUser));
        setUser(mockUser);
        return;
      }

      const result = await signIn({ username, password });

      if (result.isSignedIn) {
        const cognitoUser = await buildUserFromSession();
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(cognitoUser));
        setUser(cognitoUser);
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        throw new Error('Password change required. Please contact administrator.');
      } else {
        throw new Error('Sign in failed');
      }
    } catch (error: any) {
      localStorage.removeItem(AUTH_USER_KEY);
      setUser(null);

      if (error.name === 'NotAuthorizedException') {
        throw new Error('Incorrect username or password');
      } else if (error.name === 'UserNotFoundException') {
        throw new Error('User not found');
      } else if (error.name === 'UserNotConfirmedException') {
        throw new Error('Please verify your email first');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithFederate = useCallback(async (): Promise<void> => {
    if (!isFederationEnabled) {
      throw new Error('Federation is not configured. Set REACT_APP_COGNITO_DOMAIN.');
    }
    // If we already have a valid session (e.g. user clicked the button after
    // a previous successful sign in), don't try to start a new redirect —
    // Amplify throws "There is already a signed in user". Just refresh the
    // local user record so React updates.
    try {
      await getCurrentUser();
      const cognitoUser = await buildUserFromSession();
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(cognitoUser));
      setUser(cognitoUser);
      return;
    } catch {
      // No active session — fall through to the redirect flow.
    }
    // Amplify will redirect the browser to Cognito's hosted UI with
    // ?identity_provider=Federate. After Midway+Cognito, the browser
    // returns to /callback?code=... and Amplify finishes the exchange.
    await signInWithRedirect({ provider: { custom: 'Federate' } });
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      localStorage.removeItem(AUTH_USER_KEY);
      setUser(null);

      if (isConfigValid) {
        // global=false keeps the local sign-out; for full Cognito hosted-UI
        // sign-out (clears the SSO cookie) Amplify also redirects to the
        // configured redirectSignOut URL.
        await signOut();
      }
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem(AUTH_USER_KEY);
      setUser(null);
    }
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!isConfigValid) {
      return 'mock-token';
    }

    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }, []);

  const isAuthenticated = user !== null;
  const isAdmin = user?.role === 'admin';

  const contextValue = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isAdmin,
      isLoading,
      isFederationEnabled,
      login,
      loginWithFederate,
      logout,
      getToken,
    }),
    [user, isAuthenticated, isAdmin, isLoading, login, loginWithFederate, logout, getToken]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
